import React, { useEffect, useRef, useState } from "react";

import { Typography } from "@mui/material";
import { NumberInput } from "components/forms";

import { COLOR_BG_SUBTLE, COLOR_INFO, COLOR_PRIMARY, COLOR_SECONDARY, COLOR_VALUE } from "./colors";

interface AumInlineEditorProps {
  value: number;
  canEdit: boolean;
  onSave: (next: number) => void;
  onEditStart?: () => void;
  // Display font size differs by host (16px in the drift header, 15px in the objectives row).
  displayFontSize?: number;
}

// Click-to-edit currency field for a slice's AUM. Owns the edit state, focus, and
// Enter/Escape/blur handling; hosts wrap it in their own layout (label, row, error).
export const AumInlineEditor: React.FC<AumInlineEditorProps> = ({
  value,
  canEdit,
  onSave,
  onEditStart,
  displayFontSize = 16
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  useEffect(() => {
    if (!editing) return;
    setDraft(String(value));
    const id = requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    });
    return () => cancelAnimationFrame(id);
  }, [editing, value]);

  const startEdit = () => {
    if (!canEdit) return;
    onEditStart?.();
    setEditing(true);
  };

  const commit = () => {
    setEditing(false);
    const parsed = parseFloat(draft.trim().replace(/[^\d.]/g, ""));
    // Skip the save for empty/zero input or when the value is unchanged.
    if (Number.isFinite(parsed) && parsed > 0 && parsed !== value) onSave(parsed);
  };

  const cancel = () => setEditing(false);

  if (editing) {
    return (
      <NumberInput
        inputRef={inputRef}
        name="aum"
        value={draft}
        onChange={(e) => setDraft((e.target as HTMLInputElement).value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit();
          } else if (e.key === "Escape") {
            e.preventDefault();
            cancel();
          }
        }}
        onBlur={commit}
        size="small"
        numberInputProps={{ decimalScale: 2, allowNegative: false }}
        InputProps={{
          endAdornment: (
            <Typography sx={{ color: COLOR_SECONDARY, fontSize: "13px", lineHeight: "20px", ml: "4px" }}>USD</Typography>
          ),
          sx: {
            fontSize: "15px",
            fontWeight: 600,
            color: COLOR_PRIMARY,
            "& input": { textAlign: "right", padding: "4px 6px" },
            "& fieldset": { borderColor: COLOR_INFO }
          }
        }}
        sx={{ width: 130 }}
      />
    );
  }

  return (
    <Typography
      component={canEdit ? "button" : "span"}
      onClick={canEdit ? startEdit : undefined}
      sx={{
        fontSize: `${displayFontSize}px`,
        fontWeight: 600,
        lineHeight: "20px",
        color: COLOR_VALUE,
        whiteSpace: "nowrap",
        background: "none",
        border: "none",
        padding: "2px 6px",
        borderRadius: "4px",
        cursor: canEdit ? "pointer" : "default",
        "&:hover": canEdit ? { backgroundColor: COLOR_BG_SUBTLE } : undefined
      }}
    >
      {new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(Number(value))}
    </Typography>
  );
};
